// Kapcsolatfelvétel és hibabejelentés.
//
// MIÉRT ŰRLAP, ÉS NEM `mailto:`. A nyitóoldalon eddig egyetlen `mailto:` link
// volt. Az a látogató levelezőprogramjától függ, és ha nem indul el — mobilon
// gyakori —, a bejelentés nyomtalanul elveszik. Hibabejelentésnél ez a
// legrosszabb, ami történhet: a bejelentő azt hiszi, szólt.
//
// A beküldés a `sendContactMessage` függvényen megy át, ami előbb adatbázisba
// ír, utána küld e-mailt — így egy szolgáltató-kimaradás nem nyeli el a
// bejelentést.
//
// A SZÖVEG MAGYAR, a jogi oldalakhoz hasonlóan: a beküldéshez tartozó
// adatkezelési tájékoztatás magyar jog szerinti nyilatkozat, és a címzett is
// magyar cég.
//
// MIÉRT VAN MELLETTE OLDALSÁV. Az első változat egy cím volt, alatta hat
// mezővel. Egy üres űrlap nem mondja meg, mi történik a beküldés után, és nem
// ad alternatívát annak, aki inkább ír vagy telefonál — így a lap azt sugallta,
// hogy a beküldés a semmibe megy. Az oldalsáv ezt pótolja: mi lesz az üzenettel,
// és hol érhetők el közvetlenül.

import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { h, icon, icons } from '../lib/dom';
import { metaEsemeny } from '../lib/meta-pixel';

type Tipus = 'kapcsolat' | 'hiba';

const CEG = {
  nev: 'BLAZE DATA SOLUTION Kft.',
  szekhely: '7636 Pécs, Gadó utca 8. TT 7. ajtó',
  email: 'balazs.lang@blazedata.hu',
};

/// Mi történik a beküldés után. SZÁNDÉKOSAN NINCS benne válaszidő-ígéret:
/// azt csak az üzemeltető vállalhatja, és egy be nem tartott ígéret többet
/// árt, mint amennyit a szöveg használ.
const LEPESEK: { cim: string; szoveg: string; ikon: string }[] = [
  {
    cim: 'Megérkezik hozzánk',
    szoveg: 'A beküldés azonnal rögzül, még mielőtt bármilyen levél elindulna.',
    ikon: icons.check,
  },
  {
    cim: 'Elolvassuk',
    szoveg: 'Minden üzenetet ember néz át — nem automata válasz megy vissza.',
    ikon: icons.eye,
  },
  {
    cim: 'Megkeresünk',
    szoveg: 'A megadott e-mail-címen vagy telefonszámon jelentkezünk.',
    ikon: icons.comment,
  },
];

function mezo(cimke: string, beviteli: HTMLElement, sugo?: string): HTMLElement {
  return h(
    'label',
    { class: 'col g4' },
    h('span', { class: 'caption', text: cimke }),
    beviteli,
    ...(sugo ? [h('span', { class: 'caption ts', text: sugo })] : []),
  );
}

export function contactPage(options: {
  onBack: () => void;
  kezdoTipus?: Tipus;
}): HTMLElement {
  let tipus: Tipus = options.kezdoTipus ?? 'kapcsolat';
  let kuldes = false;

  const nev = h('input', { class: 'input', type: 'text', maxlength: '100' }) as HTMLInputElement;
  const email = h('input', { class: 'input', type: 'email', maxlength: '200' }) as HTMLInputElement;
  const telefon = h('input', { class: 'input', type: 'tel', maxlength: '40' }) as HTMLInputElement;
  const uzenet = h('textarea', {
    class: 'input ta',
    rows: '7',
    maxlength: '5000',
  }) as HTMLTextAreaElement;

  // REJTETT MEZŐ a robotoknak. Ember nem látja, tehát nem tölti ki; az
  // űrlapot vakon kitöltő robot igen. A szerver ilyenkor csendben eldobja a
  // beküldést — visszajelzés nélkül, hogy ne lehessen belőle tanulni.
  //
  // Nem `display:none`: néhány robot kifejezetten kihagyja az így elrejtett
  // mezőket. Ez a megoldás a képernyőn kívülre teszi, és a képernyőolvasók
  // elől az `aria-hidden` + `tabindex` rejti el.
  const csapda = h('input', {
    type: 'text',
    name: 'weboldal',
    tabindex: '-1',
    autocomplete: 'off',
    'aria-hidden': 'true',
    style: { position: 'absolute', left: '-9999px', width: '1px', height: '1px' },
  }) as HTMLInputElement;

  const hibaSav = h('div', { class: 'col', style: { display: 'none' } });
  const gomb = h('button', {
    class: 'btn btn-primary btn-lg',
    type: 'submit',
  }) as HTMLButtonElement;

  function frissitGomb(): void {
    gomb.textContent = kuldes ? 'Küldés…' : 'Üzenet küldése';
    gomb.disabled = kuldes;
  }

  function hiba(szoveg: string): void {
    hibaSav.textContent = szoveg;
    hibaSav.style.display = szoveg ? 'block' : 'none';
    hibaSav.style.color = 'var(--err)';
  }

  // A TÍPUSVÁLASZTÓ szegmentált vezérlő (`.seg`), nem két külön gomb. Két
  // gombnál nem látszott, hogy ez egy választás két állapota — úgy nézett ki,
  // mint egy kiemelt és egy mellékes művelet.
  const fulek: [Tipus, string][] = [
    ['kapcsolat', 'Kapcsolatfelvétel'],
    ['hiba', 'Hibabejelentés'],
  ];

  function alairas(): string {
    return tipus === 'hiba'
      ? 'Mit csináltál, mit vártál, és mi történt helyette? Ha tudod, írd le a lépéseket — minden részlet gyorsítja a javítást.'
      : 'Miben segíthetünk?';
  }

  const valaszto = h(
    'div',
    { class: 'seg', role: 'tablist', 'aria-label': 'Megkeresés típusa' },
    ...fulek.map(([ertek, cimke]) =>
      h('button', {
        class: ertek === tipus ? 'on' : '',
        type: 'button',
        role: 'tab',
        'aria-selected': String(ertek === tipus),
        text: cimke,
        onClick: () => {
          tipus = ertek;
          for (const [i, gy] of [...valaszto.querySelectorAll('button')].entries()) {
            const kivalasztott = fulek[i]![0] === tipus;
            gy.className = kivalasztott ? 'on' : '';
            gy.setAttribute('aria-selected', String(kivalasztott));
          }
          uzenet.placeholder = alairas();
        },
      }),
    ),
  );
  uzenet.placeholder = alairas();
  frissitGomb();

  const kesz = h(
    'div',
    { class: 'card pad24 col g8', style: { display: 'none' } },
    h(
      'span',
      { class: 'row g8', style: { alignItems: 'center', color: 'var(--ok)' } },
      icon(icons.check, 18),
      h('span', { class: 'h3', text: 'Megkaptuk, köszönjük.' }),
    ),
    h('span', {
      class: 'body-sm ts',
      text: 'Ha megadtad az elérhetőségedet, azon keressük meg a válasszal.',
    }),
  );

  const urlap = h(
    'form',
    {
      class: 'card pad24 col g16',
      onSubmit: async (e: Event) => {
        e.preventDefault();
        if (kuldes) return;
        hiba('');

        // A KLIENSOLDALI ELLENŐRZÉS A KÉNYELEMÉRT van, nem a biztonságért:
        // a szerver ugyanezt újra ellenőrzi. Itt csak azért nézzük, hogy a
        // látogatónak ne kelljen megvárnia egy hálózati kört a válaszért.
        if (!nev.value.trim()) return hiba('Add meg a neved.');
        if (!email.value.trim() && !telefon.value.trim()) {
          return hiba('Adj meg egy e-mail-címet vagy telefonszámot, hogy válaszolni tudjunk.');
        }
        if (!uzenet.value.trim()) return hiba('Írd le, miben segíthetünk.');

        kuldes = true;
        frissitGomb();
        try {
          const hivas = httpsCallable<Record<string, string>, { ok?: boolean }>(
            functions,
            'sendContactMessage',
          );
          await hivas({
            tipus,
            nev: nev.value,
            email: email.value,
            telefon: telefon.value,
            uzenet: uzenet.value,
            weboldal: csapda.value,
          });
          // KONVERZIÓ A META FELÉ — CSAK A MEGKERESÉSRE, A HIBAJELENTÉSRE NEM.
          //
          // A hirdetések a bemutatókérésre optimalizálnak. A hibabejelentés
          // ugyanezen az űrlapon érkezik, de az NEM érdeklődés: ha azt is
          // `Lead`-ként küldenénk, a hirdetési rendszer olyan emberekre
          // tanulna rá, akik már ügyfelek, és bajuk van.
          //
          // A HELY SEM MINDEGY: a `hivas` már lefutott, tehát a megkeresés
          // adatbázisba került. Hibánál a `catch` ágra kerülünk, ide nem — és
          // a `kuldes` jelző a dupla kattintást is kizárja. Így egy beküldés
          // pontosan egy eseményt jelent.
          if (tipus === 'kapcsolat') {
            metaEsemeny('Lead', { content_name: 'blaze_demo_request' });
          }
          urlap.style.display = 'none';
          kesz.style.display = 'flex';
        } catch (err) {
          // A SZERVER ÜZENETÉT változtatás nélkül mutatjuk: ott tudják, mi a
          // baj (hiányzó mező, túl sok beküldés), és az mind olyasmi, amivel
          // a látogató kezdeni tud valamit.
          const uzi = err instanceof Error && err.message ? err.message : '';
          hiba(uzi || 'A küldés nem sikerült. Próbáld újra, vagy írj a ' + CEG.email + ' címre.');
        } finally {
          kuldes = false;
          frissitGomb();
        }
      },
    },
    valaszto,
    mezo('Név', nev),
    mezo('E-mail-cím', email, 'E-mail vagy telefonszám közül legalább az egyik kötelező.'),
    mezo('Telefonszám', telefon),
    mezo('Üzenet', uzenet),
    csapda,
    hibaSav,
    gomb,
    // A TÁJÉKOZTATÁS a gomb MELLETT van, nem apróbetűvel a lap alján: aki
    // beküld, annak a beküldés pillanatában kell tudnia, ki kapja meg.
    h(
      'span',
      { class: 'caption ts' },
      `A beküldött adatokat a ${CEG.nev} kapja meg, és kizárólag a megkeresésed ` +
        'megválaszolására használja. Részletek: ',
      h('a', { href: '#/adatkezeles', text: 'Adatkezelési tájékoztató' }),
    ),
  );

  const oldalsav = h(
    'aside',
    { class: 'col g16', style: { minWidth: '0' } },
    h(
      'div',
      { class: 'card pad20 col g16' },
      h('span', { class: 'label ts', text: 'MI TÖRTÉNIK EZUTÁN' }),
      ...LEPESEK.map((l) =>
        h(
          'div',
          { class: 'row g12', style: { alignItems: 'flex-start' } },
          h(
            'span',
            {
              class: 'ic',
              style: {
                width: '30px',
                height: '30px',
                borderRadius: 'var(--r-sm)',
                background: 'var(--accent-soft)',
                color: 'var(--accent)',
              },
            },
            icon(l.ikon, 15),
          ),
          h(
            'div',
            { class: 'col g4 grow' },
            h('span', { style: { fontWeight: '600', fontSize: '14px' }, text: l.cim }),
            h('span', { class: 'caption ts', text: l.szoveg }),
          ),
        ),
      ),
    ),
    h(
      'div',
      { class: 'card pad20 col g12' },
      h('span', { class: 'label ts', text: 'KÖZVETLENÜL' }),
      h(
        'div',
        { class: 'col g4' },
        h('a', {
          style: { fontWeight: '600' },
          href: `mailto:${CEG.email}`,
          text: CEG.email,
          // Ugyanaz, mint a nyitooldalon: a `mailto` csak annyit mond, hogy a
          // latogato elinditotta a levelezoprogramjat -- ezert `Contact`, nem
          // `Lead`.
          onClick: () => {
            metaEsemeny('Contact', { content_name: 'blaze_demo_request_mailto' });
          },
        }),
        h('span', { class: 'caption ts', text: 'Ha inkább levelet írnál.' }),
      ),
      h('div', { class: 'hr' }),
      h(
        'div',
        { class: 'col g4' },
        h('span', { style: { fontWeight: '600', fontSize: '14px' }, text: CEG.nev }),
        h('span', { class: 'caption ts', text: CEG.szekhely }),
      ),
    ),
  );

  return h(
    'div',
    { class: 'col', style: { minHeight: '100%', background: 'var(--bg)' } },
    h(
      'main',
      {
        class: 'col g24',
        style: { maxWidth: '1000px', width: '100%', margin: '0 auto', padding: '32px 16px 64px' },
      },
      h('a', {
        class: 'caption',
        href: '#/',
        text: '← Vissza a főoldalra',
        onClick: (e: Event) => {
          e.preventDefault();
          options.onBack();
        },
      }),
      h(
        'div',
        { class: 'col g8', style: { maxWidth: '640px' } },
        h('span', { class: 'label ts', text: 'KAPCSOLAT' }),
        h('h1', { class: 'display', text: 'Írj nekünk — elolvassuk' }),
        h('p', {
          class: 'body-lg ts',
          style: { margin: '0' },
          text:
            'Kérdés, kérés vagy hiba: ugyanitt. A hibabejelentéseket külön ' +
            'kezeljük, és minden részlet segít, amit le tudsz írni.',
        }),
      ),
      // Két oszlop asztali szélességen, egy oszlop mobilon. A `c2` rács a
      // dizájnrendszerből jön, tehát a töréspont is onnan — nem itt találjuk ki.
      h(
        'div',
        { class: 'grid c2', style: { alignItems: 'start' } },
        h('div', { class: 'col g16', style: { minWidth: '0' } }, urlap, kesz),
        oldalsav,
      ),
    ),
  );
}
